import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Wrench, ShieldCheck, Clock, XCircle, AlertTriangle, CheckCircle2, Circle, Volume2, VolumeX } from "lucide-react";
import { api, ago } from "@/lib/api";
import { Pill } from "@/components/common";

const STEPS = [["detected", "Detected", Search], ["fixing", "Fixing", Wrench], ["verified", "Verified", ShieldCheck]];
const TERMINAL = {
  awaiting_approval: ["Awaiting approval", "waiting", Clock], failed: ["Repair failed", "critical", XCircle],
  escalated: ["Escalated to a human", "critical", AlertTriangle], closed: ["Closed", "unknown", CheckCircle2],
  resolved: ["Resolved manually", "healthy", CheckCircle2], cleared: ["Cleared on its own (no repair ran)", "healthy", CheckCircle2],
};

function Stepper({ item }) {
  const hasTasks = item.tasks.length > 0;
  const reached = item.stage === "verified" ? 3 : hasTasks ? 2 : 1;
  const last = item.tasks[item.tasks.length - 1];
  const times = [item.first_observed, last?.created_at, item.verification?.at];
  return (
    <div className="flex items-center gap-1" data-testid={`feed-stepper-${item.id}`}>
      {STEPS.map(([k, l, I], idx) => {
        const on = idx < reached;
        const active = item.stage === k && k === "fixing";
        return (
          <div key={k} className="flex items-center gap-1">
            {idx > 0 && <div className={`w-6 h-px ${on ? "bg-green-600" : "bg-gray-300"}`} />}
            <div className={`flex items-center gap-1 px-2 py-0.5 text-[11px] border rounded-sm ${on ? (k === "verified" ? "border-green-300 bg-green-50 text-green-800" : "border-gray-300 bg-white text-gray-800") : "border-dashed border-gray-200 text-gray-400"}`} data-testid={`feed-step-${k}-${item.id}`} title={times[idx] || ""}>
              {on ? <I className={`w-3 h-3 ${active ? "animate-pulse text-blue-600" : ""}`} /> : <Circle className="w-3 h-3" />}{l}
            </div>
          </div>
        );
      })}
    </div>
  );
}

let audioCtx = null;
function playAlert() {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === "suspended") audioCtx.resume();
    [0, 0.22].forEach((start) => {
      const o = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      o.type = "square";
      o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, audioCtx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(0.15, audioCtx.currentTime + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + start + 0.18);
      o.connect(g).connect(audioCtx.destination);
      o.start(audioCtx.currentTime + start);
      o.stop(audioCtx.currentTime + start + 0.2);
    });
  } catch (e) {
    // audio unavailable; pop-up still shown
  }
}

function useCriticalAlerts(items, soundOn, orgId) {
  const seen = useRef(null);
  const nav = useNavigate();
  useEffect(() => {
    seen.current = null;
  }, [orgId]);
  useEffect(() => {
    if (!items) return;
    const crit = items.filter((i) => i.severity === "critical");
    if (seen.current === null) {
      seen.current = new Set(crit.map((i) => i.id));
      return;
    }
    const fresh = crit.filter((i) => !seen.current.has(i.id));
    fresh.forEach((i) => {
      seen.current.add(i.id);
      toast.error(`Critical: ${i.title}`, {
        description: `${i.hostname} · incident #${i.number}`, duration: 15000,
        action: { label: "Open", onClick: () => nav(`/portal/incidents/${i.id}`) },
      });
    });
    if (fresh.length && soundOn) playAlert();
  }, [items, soundOn, nav]);
}

export function RepairFeed({ orgId }) {
  const [items, setItems] = useState(null);
  const [updated, setUpdated] = useState(null);
  const [onlyAuto, setOnlyAuto] = useState(false);
  const [soundOn, setSoundOn] = useState(localStorage.getItem("cr_alert_sound") !== "off");
  useCriticalAlerts(items, soundOn, orgId);
  const toggleSound = () => {
    const v = !soundOn;
    setSoundOn(v);
    localStorage.setItem("cr_alert_sound", v ? "on" : "off");
    if (v) playAlert();
  };
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/activity/repairs?limit=20").then((r) => { if (alive) { setItems(r.data.items); setUpdated(new Date()); } }).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => { alive = false; clearInterval(t); };
  }, [orgId]);
  const shown = (items || []).filter((i) => !onlyAuto || i.automatic);
  return (
    <div className="card-flat p-6 mt-8" data-testid="repair-feed">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-bold flex items-center gap-2">
          <span className="relative flex w-2.5 h-2.5"><span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-60 animate-ping" /><span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-red-600" /></span>
          Live repair activity
        </h3>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <button onClick={toggleSound} className="flex items-center gap-1 px-2 py-1 border border-gray-300 hover:border-gray-900" data-testid="feed-sound-toggle" aria-pressed={soundOn}>
            {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}{soundOn ? "Critical alert sound on" : "Sound muted"}
          </button>
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={onlyAuto} onChange={(e) => setOnlyAuto(e.target.checked)} data-testid="feed-auto-only-checkbox" />Automatic repairs only</label>
          <span data-testid="feed-updated">Updated {updated ? ago(updated.toISOString()) : "…"} · refreshes every 10s</span>
        </div>
      </div>
      {items === null ? <div className="loader-bar" /> : shown.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="feed-empty">No repair activity yet. When an agent reports a problem, you'll see it move from Detected → Fixing → Verified here.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {shown.map((i) => {
            const term = TERMINAL[i.stage];
            const last = i.tasks[i.tasks.length - 1];
            return (
              <li key={i.id} className="py-3 flex flex-col lg:flex-row lg:items-center gap-3 justify-between fade-up" data-testid={`feed-item-${i.id}`}>
                <div className="min-w-0">
                  <Link to={`/portal/incidents/${i.id}`} className="font-semibold text-sm hover:text-red-600" data-testid={`feed-link-${i.id}`}>#{i.number} {i.title}</Link>
                  <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-1 items-center">
                    <span>{i.hostname}</span>
                    {last && <span className="font-mono">{last.playbook_id}</span>}
                    <span>{i.automatic ? "automatic (policy)" : last ? `manual · ${last.initiated_by}` : "no action yet"}</span>
                    <span>{ago(i.updated_at)}</span>
                    {i.agent_type === "test-harness" && <Pill tone="waiting">synthetic</Pill>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Stepper item={i} />
                  {term && <Pill tone={term[1]} testid={`feed-terminal-${i.id}`}>{term[0]}</Pill>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function RealDeviceCard({ v }) {
  const steps = [
    ["Real Windows agent enrolled", v.real_device_enrolled],
    ["Real telemetry received", v.real_telemetry],
    ["Verified automatic repair on a real device", !!v.verified_real_repair],
  ];
  return (
    <div className="card-flat p-6" data-testid="real-device-card">
      <h3 className="font-bold">Real device validation</h3>
      <ul className="mt-3 space-y-2 text-sm">
        {steps.map(([l, ok], idx) => (
          <li key={l} className="flex items-center gap-2" data-testid={`real-step-${idx}`}>
            {ok ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <Circle className="w-4 h-4 text-gray-300" />}
            <span className={ok ? "text-gray-900" : "text-gray-500"}>{l}</span>
          </li>
        ))}
      </ul>
      {v.verified_real_repair ? (
        <p className="text-xs text-green-700 mt-3" data-testid="real-repair-proof">{v.verified_real_repair.playbook_id} verified on {v.verified_real_repair.hostname} · {ago(v.verified_real_repair.completed_at)}</p>
      ) : (
        <Link to="/docs/real-device-test" className="text-xs text-red-600 underline mt-3 inline-block" data-testid="real-device-guide-link">Step-by-step real device test →</Link>
      )}
    </div>
  );
}
